import { api } from '@/lib/axios-client'
import { trackSitePlanJobsStarted } from '@/lib/monitoring/analytics'
import type {
  CheckProgressPayload,
  DocumentTrackerProgress,
} from '@/features/city-clerk/shared'
export type { DocumentTrackerProgress }

export type AddSitePlanDto = {
  permitSubmittalId?: string
  callbackUrl?: string
}

type SitePlanJobsRawResponse = {
  trackerId?: string
  // checkId is populated only on the Check Orchestration v2 path
  // (Phase 2c onward). When present, frontend treats this as a v2
  // response: subscribe via subscribeToCheckSignalR + bootstrap
  // documents from the attachments REST endpoint instead of from
  // documentTrackerIds (which is empty on v2).
  checkId?: string
  // checkIds carries every check the heavy orchestrator started
  // for this submittal (site-plan-check + qc-alta-title +
  // qc-tenant-improvement, in whichever combination the template's
  // slot kinds implied). Older backend builds emit only checkId;
  // when checkIds is missing or empty, fall back to [checkId].
  checkIds?: string[]
  documentTrackerIds?: string[]
  totalJobsScheduled?: number
  totalJobs?: number
  allScheduledSuccessfully?: boolean
  jobIds?: Record<string, string>
  jobStatuses?: Record<string, string>
  message?: string
} | null

export type SitePlanJobsResult = {
  trackerId?: string
  // v2 Check identifier — set when backend's
  // CheckOrchestrationV2Enabled flag is on. When non-empty, the
  // LoadingPage must use the v2 SignalR subscription path.
  checkId?: string
  // Full set of v2 checks started for this submittal. The loading
  // page subscribes to every entry so progress for non-Site-Plan
  // checks (qc-alta-title, qc-tenant-improvement) is visible.
  // Falls back to [checkId] when the backend omits it (older
  // builds during a rollout window).
  checkIds?: string[]
  documentTrackerIds?: string[]
  totalJobs?: number
  allScheduledSuccessfully?: boolean
  message?: string
  documentProgress?: Record<string, DocumentTrackerProgress>
  documentTrackerMap?: Record<string, string>
}

const sitePlanTrackerStorageKey = (permitSubmittalId: string) =>
  `site-plan-tracker:${permitSubmittalId}`

export const cacheSitePlanTracker = (
  permitSubmittalId: string,
  tracker: SitePlanJobsResult,
): void => {
  if (!permitSubmittalId) return
  sessionStorage.setItem(
    sitePlanTrackerStorageKey(permitSubmittalId),
    JSON.stringify(tracker),
  )
}

export const getCachedSitePlanTracker = (
  permitSubmittalId: string,
): SitePlanJobsResult => {
  if (!permitSubmittalId) return {}
  const raw = sessionStorage.getItem(
    sitePlanTrackerStorageKey(permitSubmittalId),
  )
  if (!raw) return {}
  try {
    return (JSON.parse(raw) as SitePlanJobsResult) ?? {}
  } catch {
    return {}
  }
}

export const clearCachedSitePlanTracker = (permitSubmittalId: string): void => {
  if (!permitSubmittalId) return
  sessionStorage.removeItem(sitePlanTrackerStorageKey(permitSubmittalId))
}

export const updateCachedDocumentProgress = (
  permitSubmittalId: string,
  progress: DocumentTrackerProgress,
): void => {
  const current = getCachedSitePlanTracker(permitSubmittalId)
  cacheSitePlanTracker(permitSubmittalId, {
    ...current,
    documentProgress: {
      ...(current.documentProgress ?? {}),
      [progress.documentTrackerId]: progress,
    },
  })
}

export const setCachedDocumentTrackerMap = (
  permitSubmittalId: string,
  map: Record<string, string>,
): void => {
  const current = getCachedSitePlanTracker(permitSubmittalId)
  cacheSitePlanTracker(permitSubmittalId, {
    ...current,
    documentTrackerMap: map,
  })
}

// Reconciliation fetch — pulls the current snapshot of a v2 Check
// the same shape SignalR pushes. Used on LoadingPage mount so a
// client that missed the SignalR window (cv-core cache HIT, slow
// connect, page reload mid-run) still paints the latest state
// instead of an empty Pending widget. Returns null on 404 so callers
// can drop checks that no longer exist (e.g. stale cache references)
// without surfacing the error.
export const getCheckProgress = async (
  checkId: string,
): Promise<CheckProgressPayload | null> => {
  if (!checkId) return null
  try {
    const response = await api.get<CheckProgressPayload>(
      `/Check/${checkId}/progress`,
      // 404 is the legitimate "no such check" signal; we route to the
      // null-return path without surfacing a toast or redirect.
      { meta: { handle404: 'ignore' } } as never,
    )
    return response ?? null
  } catch {
    return null
  }
}

export const getCheckProgressBySubmittal = async (
  submittalId: string,
): Promise<CheckProgressPayload[] | null> => {
  if (!submittalId) return null
  try {
    const response = await api.get<CheckProgressPayload[]>(
      `/Check/submittal/${submittalId}/progress`,
      { meta: { handle404: 'ignore' } } as never,
    )
    return response ?? null
  } catch {
    return null
  }
}

export type PermitProgressSummary = {
  permitId: string
  inProgressChecks: number
  status: string
}

export type ProjectProgressSummary = {
  projectId: string
  inProgressChecks: number
  status: string
  permits: PermitProgressSummary[]
}

export const getCheckProgressByProject = async (
  projectId: string,
): Promise<ProjectProgressSummary | null> => {
  if (!projectId) return null
  try {
    const response = await api.get<ProjectProgressSummary>(
      `/Check/project/${projectId}/progress`,
      { meta: { handle404: 'ignore' } } as never,
    )
    return response ?? null
  } catch {
    return null
  }
}

export const callSitePlanJobs = async (
  data: AddSitePlanDto,
): Promise<SitePlanJobsResult | null> => {
  const response = await api.post<SitePlanJobsRawResponse>(
    '/SitePlanJobs/SitePlansJobs',
    data,
  )
  if (!response) return null

  // Prefer the explicit checkIds list when the backend supplies one;
  // fall back to [checkId] so a frontend deployed ahead of the backend
  // upgrade still subscribes to at least the first check (rollout-safe).
  const resolvedCheckIds =
    response.checkIds && response.checkIds.length > 0
      ? response.checkIds
      : response.checkId
        ? [response.checkId]
        : []

  const result: SitePlanJobsResult = {
    trackerId: response.trackerId,
    checkId: response.checkId,
    checkIds: resolvedCheckIds,
    documentTrackerIds: response.documentTrackerIds ?? [],
    totalJobs: response.totalJobsScheduled ?? response.totalJobs,
    allScheduledSuccessfully: response.allScheduledSuccessfully,
    message: response.message,
  }

  // Cache when EITHER identifier is present so the LoadingPage
  // can route into the right subscription path on next render.
  if (data.permitSubmittalId && (result.trackerId || result.checkId)) {
    cacheSitePlanTracker(data.permitSubmittalId, result)
  }

  if (data.permitSubmittalId) {
    trackSitePlanJobsStarted(data.permitSubmittalId, result.totalJobs)
  }

  return result
}

export type PermitSubmittalRecognitionDoc = {
  type: string
  urls: { attachmentId: string; url?: string }[]
}

type StartPermitSubmittalRecognitionDto = {
  permitSubmittalId: string
  docs: PermitSubmittalRecognitionDoc[]
  callbackUrl?: string
}

type PermitSubmittalRecognitionStartResult = { checkId: string }

// Runs only qpoint-core's drawing-scale recognition — not the full
// SitePlanCheck cv-core pipeline callSitePlanJobs triggers above.
export const callPermitSubmittalRecognition = async (
  data: StartPermitSubmittalRecognitionDto,
): Promise<PermitSubmittalRecognitionStartResult | null> => {
  const response = await api.post<PermitSubmittalRecognitionStartResult>(
    '/PermitSubmittalRecognition/Start',
    data,
  )
  return response ?? null
}
